/**
 * Lindy Blog Sidebar - Dynamic Data Loader
 * Fetches recent posts and categories from Shopify Ajax API
 */

class BlogSidebarLoader {
  constructor(blogHandle) {
    // Always fetch from 'blog' blog for recent posts, not case studies
    this.blogHandle = 'blog';
    this.sidebar = document.querySelector('.blog-sidebar');
    if (this.sidebar) {
      this.init();
    }
  }

  async init() {
    try {
      await Promise.all([
        this.loadRecentPosts(),
        this.loadCategories()
      ]);
      // Hide loading overlay after data is loaded
      this.hideLoading();
    } catch (error) {
      console.error('Error loading blog sidebar data:', error);
      // Hide loading even if there's an error
      this.hideLoading();
    }
  }

  hideLoading() {
    const loadingOverlay = this.sidebar.querySelector('.blog-sidebar__loading');
    if (loadingOverlay) {
      loadingOverlay.classList.add('hidden');
      // Remove from DOM after animation completes
      setTimeout(() => {
        loadingOverlay.remove();
      }, 300);
    }
  }

  async loadRecentPosts() {
    try {
      // Fetch recent blog articles using Shopify's JSON endpoint
      const response = await fetch(`/blogs/${this.blogHandle}?view=json`);

      if (!response.ok) {
        throw new Error('Failed to fetch blog posts');
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Try to extract articles from the response
      // This is a fallback approach since Shopify doesn't have a direct JSON API for blogs
      const articles = this.extractArticlesFromHTML(doc);

      if (articles.length > 0) {
        this.renderRecentPosts(articles.slice(0, 5));
      }
    } catch (error) {
      console.error('Error loading recent posts:', error);
      // Keep the placeholder data if API fails
    }
  }

  async loadCategories() {
    try {
      // Get all unique tags by fetching blog.all_tags
      // We'll need to make multiple requests to get all article tags
      const tags = new Set();

      // First, add tags from current article
      const articleTags = this.sidebar.getAttribute('data-article-tags');
      if (articleTags && articleTags.trim()) {
        articleTags.split(',').forEach(tag => {
          const trimmed = tag.trim();
          if (trimmed) tags.add(trimmed);
        });
      }

      // Fetch blog listing to get more tags from other articles
      const response = await fetch(`/blogs/${this.blogHandle}`);

      if (response.ok) {
        const html = await response.text();
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');

        // Look for article cards and extract their tags
        const articleCards = doc.querySelectorAll('.blog-article-card');
        articleCards.forEach(card => {
          const tagsAttr = card.getAttribute('data-tags');
          if (tagsAttr) {
            tagsAttr.split(',').forEach(tag => {
              const trimmed = tag.trim();
              if (trimmed) tags.add(trimmed);
            });
          }
        });
      }

      // If still no tags found, use fallback
      if (tags.size === 0) {
        const fallbackTags = ['USB', 'HDMI', 'Cables', 'Adapters', 'DisplayPort', 'Audio'];
        fallbackTags.forEach(tag => tags.add(tag));
      }

      if (tags.size > 0) {
        this.renderCategories(Array.from(tags));
      }
    } catch (error) {
      console.error('Error loading categories:', error);
      // Use fallback if API fails
      const fallbackTags = ['USB', 'HDMI', 'Cables', 'Adapters', 'DisplayPort', 'Audio'];
      this.renderCategories(fallbackTags);
    }
  }

  extractArticlesFromHTML(doc) {
    const articles = [];

    // Look for article title links specifically
    const articleTitles = doc.querySelectorAll('.blog-article-card__title');
    const seen = new Set();

    articleTitles.forEach(titleElement => {
      const title = titleElement.textContent.trim();
      // Find the parent article card and get the button link
      const card = titleElement.closest('.blog-article-card');
      if (card) {
        const link = card.querySelector('.blog-article-card__button');
        if (link) {
          const href = link.getAttribute('href');
          if (href && title && !seen.has(href)) {
            articles.push({ url: href, title: title });
            seen.add(href);
          }
        }
      }
    });

    return articles;
  }

  renderRecentPosts(articles) {
    const postsContainer = this.sidebar.querySelector('.blog-sidebar__posts');
    if (!postsContainer) return;

    postsContainer.innerHTML = articles.map(article => `
      <a href="${article.url}" class="blog-sidebar__post-link">
        <svg class="blog-sidebar__arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.62 4.28L8.95 7.61C9.04 7.70 9.04 7.84 8.95 7.93L5.62 11.26" stroke="#1C1C1C" stroke-width="1"/>
        </svg>
        <span class="blog-sidebar__post-title">${this.escapeHtml(article.title)}</span>
      </a>
    `).join('');
  }

  renderCategories(tags) {
    const categoriesContainer = this.sidebar.querySelector('.blog-sidebar__categories');
    if (!categoriesContainer) return;

    const visibleCount = 6;
    const hasMore = tags.length > visibleCount;

    if (!hasMore) {
      // If 6 or fewer tags, render them directly without show-more
      categoriesContainer.innerHTML = tags.map(tag => this.renderCategoryLink(tag)).join('');
      return;
    }

    // Wrap in show-more-component for expandable functionality
    const visibleTags = tags.slice(0, visibleCount);
    const hiddenTags = tags.slice(visibleCount);

    categoriesContainer.innerHTML = `
      <show-more-component class="show-more" data-expanded="false" data-skip-node-update="true">
        <div ref="showMoreContent">
          ${visibleTags.map(tag => this.renderCategoryLink(tag)).join('')}
          ${hiddenTags.map(tag => this.renderCategoryLink(tag, true)).join('')}
        </div>
        <button class="blog-sidebar__show-more" type="button" ref="showMoreButton" on:click="/toggle" aria-expanded="false">
          <svg class="blog-sidebar__plus" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3.33 8H12.67M8 3.33V12.67" stroke="#1C1C1C" stroke-width="1" stroke-linecap="round"/>
          </svg>
          <span class="blog-sidebar__show-more-label blog-sidebar__show-more-label--more">Show More</span>
          <span class="blog-sidebar__show-more-label blog-sidebar__show-more-label--less">Show Less</span>
        </button>
      </show-more-component>
    `;
  }

  renderCategoryLink(tag, isHidden = false) {
    const hiddenClass = isHidden ? ' hidden' : '';
    const refAttr = isHidden ? ' ref="showMoreItems[]"' : '';

    return `
      <a href="/blogs/${this.blogHandle}/tagged/${encodeURIComponent(tag)}" class="blog-sidebar__category-link${hiddenClass}"${refAttr}>
        <svg class="blog-sidebar__arrow" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M5.62 4.28L8.95 7.61C9.04 7.70 9.04 7.84 8.95 7.93L5.62 11.26" stroke="#1C1C1C" stroke-width="1"/>
        </svg>
        <span class="blog-sidebar__category-title">${this.escapeHtml(tag)}</span>
      </a>
    `;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    const blogHandle = document.querySelector('.blog-sidebar')?.dataset.blogHandle;
    new BlogSidebarLoader(blogHandle);
  });
} else {
  const blogHandle = document.querySelector('.blog-sidebar')?.dataset.blogHandle;
  new BlogSidebarLoader(blogHandle);
}
